
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { formatCurrency, formatDate } from './utils';
import { AlertIcon } from '../BorrowingsIcons';

interface RecalculatedScheduleProps {
  originalRepayments: any[];
  updatedRepayments: any[];
  onApprove: () => void;
  onReject: () => void;
}

export const RecalculatedSchedule: React.FC<RecalculatedScheduleProps> = ({
  originalRepayments,
  updatedRepayments,
  onApprove,
  onReject
}) => {
  // Show only repayments that have changed
  const changedRepayments = updatedRepayments.filter((repayment, index) => {
    const originalRepayment = originalRepayments[index];
    return repayment.recalculated;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <AlertIcon className="h-5 w-5" />
          Review Recalculated Schedule
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md bg-amber-50 p-4 border border-amber-200 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertIcon className="h-5 w-5 text-amber-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-amber-800">Please Review Changes</h3>
              <div className="mt-2 text-sm text-amber-700">
                <p>
                  The repayment schedule has been recalculated. Please review the changes
                  below before approving or rejecting the new schedule.
                </p>
              </div>
            </div>
          </div>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Original Principal</TableHead>
              <TableHead className="text-right">New Principal</TableHead>
              <TableHead className="text-right">Original Interest</TableHead>
              <TableHead className="text-right">New Interest</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {changedRepayments.map((repayment, index) => {
              const originalRepayment = originalRepayments.find(r => r.date === repayment.date) || 
                                       originalRepayments.find(r => r.id === repayment.id);
              
              return (
                <TableRow key={index}>
                  <TableCell>{formatDate(repayment.date)}</TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        repayment.status === 'Paid' ? 'outline' :
                        repayment.status === 'Prepaid' ? 'secondary' : 
                        'default'
                      }
                    >
                      {repayment.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(originalRepayment?.principal || 0)}
                  </TableCell>
                  <TableCell className={`text-right ${repayment.principal !== originalRepayment?.principal ? 'font-medium text-amber-600' : ''}`}>
                    {formatCurrency(repayment.principal)}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(originalRepayment?.interest || 0)}
                  </TableCell>
                  <TableCell className={`text-right ${repayment.interest !== originalRepayment?.interest ? 'font-medium text-amber-600' : ''}`}>
                    {formatCurrency(repayment.interest)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter className="border-t bg-muted/50 flex justify-end gap-2 pt-3 pb-3">
        <Button variant="outline" onClick={onReject}>
          Reject Changes
        </Button>
        <Button onClick={onApprove}>
          Approve Changes
        </Button>
      </CardFooter>
    </Card>
  );
};
